import { Key, ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Heading1 from "@components/layout/Heading";
import TableMultiSelection from "@components/layout/TableMultiSelection";
import { ReportColumn } from "@services/reportEngine";
import { formatCell } from "./formatCell";
import {
  makeStoredNotesCellRenderer,
  REPORT_NOTES_FIELD_NAMES,
  ReportNoteRow,
  StoredReportNotesModal,
} from "@components/notes/reportNotes";

type ReportResultViewProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  controls?: ReactNode;
  contextActions?: ReactNode;
  actions?: ReactNode;
  metadata?: ReactNode;
  columns: ReportColumn[];
  rows: Array<Record<string, any>>;
  onSelectionChange: (ids: any[]) => void;
  tableKey?: Key;
  loading?: boolean;
  tableMessage?: ReactNode;
  pageSize?: number;
  footer?: ReactNode;
};

/**
 * One result surface for ad-hoc previews, template runs and saved snapshots.
 * Callers own data and permissions; this component owns the stable visual
 * hierarchy so capability differences do not turn into different layouts.
 */
const ReportResultView: React.FunctionComponent<ReportResultViewProps> = ({
  title,
  subtitle,
  controls,
  contextActions,
  actions,
  metadata,
  columns,
  rows,
  onSelectionChange,
  tableKey,
  loading = false,
  tableMessage,
  pageSize = 100,
  footer,
}) => {
  const { t } = useTranslation();
  const [notesModal, setNotesModal] = useState<{
    title: string;
    notes: ReportNoteRow[];
  } | null>(null);
  const tableFields = useMemo(
    () =>
      columns.map(column => ({
        title: (column.displayLabel ?? t(column.label)) as string,
        name: column.name,
        cellRenderer: REPORT_NOTES_FIELD_NAMES.has(column.name)
          ? makeStoredNotesCellRenderer(
              (column.displayLabel ?? t(column.label)) as string,
              (modalTitle, notes) =>
                setNotesModal({ title: modalTitle, notes }),
              count => t("notes.indicator_title", { count }) as string,
            )
          : (value: any) =>
              formatCell(value, column.dataType, column.name),
      })),
    [columns, t],
  );

  return (
    <>
      <div className="flex grow flex-col gap-4 p-4">
        {title || subtitle ? (
          <div>
            {title ? <Heading1 light>{title}</Heading1> : null}
            {subtitle ? <div className="text-sm">{subtitle}</div> : null}
          </div>
        ) : null}

        {controls}
        {contextActions}
        {actions ? (
          <div className="flex flex-wrap justify-end gap-2">{actions}</div>
        ) : null}
        {metadata}
        {columns.some(column => column.name === "usageCost") ? (
          <p className="text-sm text-gray-600">
            {t("reporting.usage_cost_help")}
          </p>
        ) : null}

        {tableMessage ?? (
          <TableMultiSelection
            key={tableKey}
            selectionKey="_rowId"
            onSelectionChange={onSelectionChange}
            clearSelectionOnDataChanged={true}
            fields={tableFields}
            data={rows}
            pageSize={pageSize}
            loading={loading}
            containerClassName="min-h-[12rem] overflow-x-scroll"
            stickyHeader
          />
        )}
      </div>
      {footer}
      <StoredReportNotesModal
        open={Boolean(notesModal)}
        title={notesModal?.title ?? ""}
        notes={notesModal?.notes ?? []}
        onClose={() => setNotesModal(null)}
      />
    </>
  );
};

export default ReportResultView;
